import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Collapse,
  IconButton,
  Popover,
  Skeleton,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import PhoneInTalkRounded from '@mui/icons-material/PhoneInTalkRounded'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import CloseRounded from '@mui/icons-material/CloseRounded'
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded'
import RadioButtonUncheckedRounded from '@mui/icons-material/RadioButtonUncheckedRounded'
import ErrorRounded from '@mui/icons-material/ErrorRounded'
import LockOutlined from '@mui/icons-material/LockOutlined'
import SupportAgentRounded from '@mui/icons-material/SupportAgentRounded'
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded'
import CallEndRounded from '@mui/icons-material/CallEndRounded'
import MicOffRounded from '@mui/icons-material/MicOffRounded'
import MicRounded from '@mui/icons-material/MicRounded'
import { toast } from 'react-toastify'
import {
  DESK_CALL_ROLES,
  describeDeskCallStatus,
  detectDeskMicrophone,
  getDeskCall,
  hangUpDeskCall,
  joinDeskTalk,
  placeDeskCall,
  previewDeskCall,
  sendScreeningCall,
  setDeskTalkPreference,
  startDeskCallRequest,
} from 'src/clinic/services/deskCallService'
import {
  endPortalVoiceCall,
  getPortalVoiceFriendlyErrorMessage,
  subscribePortalVoiceCallState,
  togglePortalVoiceMute,
} from 'src/mentor/components/voice/portalVoiceCallManager'
import DeskCallerVerification from './DeskCallerVerification'

// Front desk → practitioner on duty. The desk never sees or picks a number: the server resolves who
// is checked in / rostered for the role and bridges the call. The popover shows who will be called
// (and who else could be), places the call, then follows the request to its outcome as a short
// timeline (ringing → answered, or no answer → messaged → on their way).

const OUTCOME_POLL_MS = 2500
const OUTCOME_POLL_MAX = 14
const ACTIVE_CALL_STATES = ['connecting', 'ringing', 'connected', 'reconnecting', 'on-hold', 'ending']

const TONES = {
  green: { fg: '#047857', bg: 'rgba(16, 185, 129, 0.12)', ring: '#10b981' },
  blue: { fg: '#1d4ed8', bg: 'rgba(59, 130, 246, 0.12)', ring: '#3b82f6' },
  amber: { fg: '#b45309', bg: 'rgba(245, 158, 11, 0.14)', ring: '#f59e0b' },
  grey: { fg: '#475569', bg: 'rgba(148, 163, 184, 0.18)', ring: '#94a3b8' },
  red: { fg: '#b91c1c', bg: 'rgba(239, 68, 68, 0.1)', ring: '#ef4444' },
}

function candidateTone(candidate) {
  if (!candidate) return TONES.grey
  if (candidate.checkedIn) return TONES.green
  if (candidate.resolvedVia === 'rostered-now') return TONES.blue
  if (candidate.notYetOnDuty || candidate.resolvedVia === 'rostered-next') return TONES.amber
  return TONES.grey
}

function initialsOf(name) {
  return (name || '?')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('')
}

function formatClock(iso) {
  if (!iso) return ''
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
}

function formatElapsed(seconds) {
  const total = Math.max(0, Math.floor(Number(seconds) || 0))
  const minutes = Math.floor(total / 60)
  return `${minutes}:${String(total % 60).padStart(2, '0')}`
}

// What the browser softphone is doing right now, in desk words.
function softphoneLabel(callState, requestInFlight) {
  switch (callState?.status) {
    case 'connecting':
      return 'Connecting…'
    case 'ringing':
      return 'Ringing…'
    case 'connected':
      return `Connected · ${formatElapsed(callState?.elapsedSeconds)}`
    case 'reconnecting':
      return 'Reconnecting…'
    case 'on-hold':
      return 'On hold'
    case 'ending':
      return 'Hanging up…'
    default:
      return requestInFlight ? 'Not connected in this browser' : ''
  }
}

function firstNameOf(candidate) {
  return candidate?.firstName || (candidate?.name || '').split(' ')[0] || ''
}

// The request's story so far, oldest first. Each step: { label, time, state: done|active|warn|error }.
function announceTimelineOf(request) {
  const name = request.targetDisplayName || 'The practitioner'
  const first = name.split(' ')[0]
  const status = request.status
  const steps = [{ label: `Screening call sent to ${name}`, time: request.createdAt, state: 'done' }]
  if (request.practitionerWhatsappSent) {
    steps.push({ label: 'WhatsApp sent with the details and a call-back link', time: request.practitionerIncomingWhatsappSentAt, state: 'done' })
  }
  if (['resolving', 'ringing'].includes(status)) {
    steps.push({ label: request.answeredAt ? `${first} is listening to the request…` : `Ringing ${first}…`, state: 'active' })
    return steps
  }
  if (request.answeredAt && !['missed', 'notified', 'escalated', 'failed', 'cancelled', 'expired'].includes(status)) {
    const plays = request.announcePlays > 1 ? ` (played ${request.announcePlays}×)` : ''
    steps.push({ label: `${first} heard the request${plays}`, time: request.answeredAt, state: 'done' })
  }
  if (request.talkRequestedAt) {
    const joined = Boolean(request.talkJoinedAt)
    const callingBack = request.status === 'called-back'
    steps.push({
      label: joined
        ? `Talking with ${first} (${request.talkJoinedVia === 'phone' ? 'on your phone' : 'on this screen'})`
        : callingBack
          ? `${first} is calling you back`
          : `${first} wants to talk to you`,
      time: joined ? request.talkJoinedAt : request.talkRequestedAt,
      state: joined ? 'done' : 'active',
    })
  }
  if (status === 'acknowledged') {
    steps.push({ label: `${first} is on the way`, time: request.acknowledgedAt, state: 'done' })
  }
  if (['missed', 'notified', 'escalated'].includes(status)) {
    steps.push({ label: request.outcomeDetail || `${first} did not respond`, state: 'warn' })
    if (request.practitionerWhatsappSent || request.notifiedAt) {
      steps.push({ label: 'Messaged on WhatsApp with a call-back link', time: request.notifiedAt, state: 'done' })
    }
  }
  if (status === 'failed') {
    steps.push({ label: 'The screening call could not be placed', detail: request.outcomeDetail, state: 'error' })
  }
  if (status === 'closed') {
    steps.push({ label: `Closed: ${request.conclusionLabel || 'done'}`, time: request.concludedAt, state: 'done' })
  }
  return steps
}

// Announce requests the desk should keep following (the server call is live or may still ask to talk).
function isAnnounceLive(request) {
  if (!request || request.mode !== 'announce') return false
  return (
    ['resolving', 'ringing'].includes(request.status) ||
    (['answered', 'called-back'].includes(request.status) && !request.endedAt)
  )
}

// Keep polling a screening request for a while after it rang out: the screener may call back from
// WhatsApp or their phone, and the desk must see "Talk now".
const ANNOUNCE_FOLLOW_MS = 45 * 60 * 1000
function isAnnounceFollowable(request) {
  if (!request || request.mode !== 'announce') return false
  if (['closed', 'cancelled', 'failed', 'expired'].includes(request.status)) return false
  const created = request.createdAt ? new Date(request.createdAt).getTime() : 0
  return isAnnounceLive(request) || (created > 0 && Date.now() - created < ANNOUNCE_FOLLOW_MS)
}

function timelineOf(request) {
  if (!request) return []
  if (request.mode === 'announce') return announceTimelineOf(request)
  const name = request.targetDisplayName || 'The practitioner'
  const steps = [{ label: 'Request sent', time: request.createdAt, state: 'done' }]
  const status = request.status
  if (request.supportLine) {
    const why =
      request.supportLineReason === 'practitioner-unreachable'
        ? `${request.unreachablePractitionerName || 'The practitioner'} is not reachable on mobile`
        : 'Nobody on duty'
    steps.push({ label: `${why} · connecting to AUMHUM support`, state: status === 'ringing' ? 'active' : 'done' })
  }
  if (status === 'resolving' || status === 'ringing') {
    steps.push({ label: `Ringing ${name}…`, state: 'active' })
    return steps
  }
  if (status === 'answered') {
    steps.push({ label: `${name} answered`, time: request.answeredAt, state: 'done' })
    return steps
  }
  if (status === 'nobody-on-duty') {
    steps.push({ label: request.outcomeDetail || 'Nobody is on duty', state: 'error' })
    return steps
  }
  if (status === 'failed') {
    steps.push({ label: 'The call could not connect', detail: request.outcomeDetail, state: 'error' })
    return steps
  }
  if (status === 'cancelled') {
    steps.push({ label: 'Cancelled before it rang through', state: 'warn' })
    return steps
  }
  if (['missed', 'notified', 'escalated', 'acknowledged', 'called-back', 'closed', 'expired'].includes(status)) {
    if (!request.answeredAt) {
      steps.push({ label: `${name} did not answer`, state: 'warn' })
    }
    if (request.practitionerWhatsappSent || request.notifiedAt) {
      steps.push({ label: 'Messaged on WhatsApp with a call-back link', time: request.notifiedAt, state: 'done' })
    }
    if (status === 'escalated') {
      steps.push({ label: request.outcomeDetail || 'Passed on to the next practitioner', state: 'active' })
    }
    if (request.acknowledgement || status === 'acknowledged') {
      steps.push({ label: `${name} is on the way`, time: request.acknowledgedAt, state: 'done' })
    }
    if (status === 'called-back') {
      steps.push({ label: `${name} called the desk back`, time: request.calledBackAt, state: 'done' })
    }
    if (status === 'closed') {
      steps.push({ label: `Closed: ${request.conclusionLabel || 'done'}`, time: request.concludedAt, state: 'done' })
    }
    if (status === 'missed' || status === 'notified') {
      steps.push({ label: 'Waiting for them to reply or call back', state: 'active' })
    }
  }
  return steps
}

function StepIcon({ state }) {
  if (state === 'done') return <CheckCircleRounded sx={{ fontSize: 18, color: TONES.green.ring }} />
  if (state === 'active') return <CircularProgress size={14} thickness={6} sx={{ m: '2px', color: TONES.blue.ring }} />
  if (state === 'warn') return <ErrorRounded sx={{ fontSize: 18, color: TONES.amber.ring }} />
  if (state === 'error') return <ErrorRounded sx={{ fontSize: 18, color: TONES.red.ring }} />
  return <RadioButtonUncheckedRounded sx={{ fontSize: 18, color: TONES.grey.ring }} />
}
StepIcon.propTypes = { state: PropTypes.string }

function CandidateRow({ candidate, selected, onSelect, disabled }) {
  const tone = candidateTone(candidate)
  return (
    <Box
      role="radio"
      aria-checked={selected}
      tabIndex={disabled ? -1 : 0}
      onClick={disabled ? undefined : onSelect}
      onKeyDown={(event) => {
        if (!disabled && (event.key === 'Enter' || event.key === ' ')) onSelect()
      }}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        p: 1,
        borderRadius: '12px',
        cursor: disabled ? 'default' : 'pointer',
        border: '1.5px solid',
        borderColor: selected ? tone.ring : 'rgba(0,0,0,0.08)',
        backgroundColor: selected ? tone.bg : '#fff',
        transition: 'border-color 0.15s ease, background-color 0.15s ease',
      }}
    >
      <Avatar sx={{ width: 36, height: 36, fontSize: 14, fontWeight: 800, bgcolor: tone.bg, color: tone.fg, border: `2px solid ${tone.ring}` }}>
        {initialsOf(candidate?.name)}
      </Avatar>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={{ fontSize: '14px', fontWeight: 800, lineHeight: 1.2 }} noWrap>
          {candidate?.name}
        </Typography>
        <Typography sx={{ fontSize: '12px', fontWeight: 600, color: tone.fg }} noWrap>
          {candidate?.statusLabel}
        </Typography>
        {candidate?.reachable === false ? (
          <Typography sx={{ fontSize: '11.5px', fontWeight: 700, color: TONES.red.fg }} noWrap>
            Not reachable on mobile · no number on profile
          </Typography>
        ) : null}
      </Box>
      {selected ? <CheckCircleRounded sx={{ fontSize: 20, color: tone.ring }} /> : <RadioButtonUncheckedRounded sx={{ fontSize: 20, color: 'rgba(0,0,0,0.25)' }} />}
    </Box>
  )
}
CandidateRow.propTypes = { candidate: PropTypes.object, selected: PropTypes.bool, onSelect: PropTypes.func, disabled: PropTypes.bool }

export default function DeskCallButton({
  clinicBrandId,
  clinicId,
  role = 'screener',
  chamberRoomId,
  eventType,
  specialtySlug,
  doctorMentorId,
  bookedEventId,
  practitionerId,
  label,
  heading,
  reason,
  contextLine,
  size = 'small',
  variant = 'outlined',
  fullWidth = false,
  compact = false,
  sx,
  onRequestChange,
  // Optional custom trigger: ({ onClick, label, busy, request }) => element. Used by booking strips.
  renderTrigger,
  // 'call' bridges the desk softphone to the practitioner; 'announce' has the server call them and read
  // the request out, with a menu to repeat it, talk to the desk (press 2) or say "on my way" (press 3).
  mode = 'call',
}) {
  const [anchorEl, setAnchorEl] = useState(null)
  const [preview, setPreview] = useState(null)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [placing, setPlacing] = useState(false)
  const [activeRequest, setActiveRequest] = useState(null)
  const [callState, setCallState] = useState(null)
  const [triedPractitionerIds, setTriedPractitionerIds] = useState([])
  const [selectedId, setSelectedId] = useState(null)
  const [showOwnPhone, setShowOwnPhone] = useState(false)
  const [talkPreference, setTalkPreference] = useState('auto')
  const [micAvailable, setMicAvailable] = useState(null)
  const [joiningTalk, setJoiningTalk] = useState(false)
  const pollRef = useRef(null)
  const announcePollRef = useRef(null)
  const lastAnchorRef = useRef(null)
  const talkAlertedRef = useRef(null)
  const isAnnounce = mode === 'announce'
  const open = Boolean(anchorEl)
  const roleMeta = DESK_CALL_ROLES[role] || DESK_CALL_ROLES.screener
  const roleWord = roleMeta.label.toLowerCase()

  useEffect(() => subscribePortalVoiceCallState(setCallState), [])
  useEffect(
    () => () => {
      if (pollRef.current) clearInterval(pollRef.current)
    },
    [],
  )

  const loadPreview = useCallback(
    async (skipIds = []) => {
      if (!clinicId) return
      setPreviewLoading(true)
      const result = await previewDeskCall({
        clinicBrandId,
        clinicId,
        role,
        chamberRoomId,
        eventType,
        specialtySlug,
        doctorMentorId,
        skipPractitionerIds: skipIds,
      })
      setPreview(result)
      if (result?.talkPreference) setTalkPreference(result.talkPreference)
      setSelectedId(practitionerId || result?.primary?.practitionerId || null)
      setPreviewLoading(false)
    },
    [clinicBrandId, clinicId, role, chamberRoomId, eventType, specialtySlug, doctorMentorId, practitionerId],
  )

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget)
    lastAnchorRef.current = event.currentTarget
    if (isAnnounce) {
      detectDeskMicrophone().then(setMicAvailable)
    }
    // Re-opening while a request is in flight keeps following it instead of starting over.
    if (!activeRequest || (!isAnnounceFollowable(activeRequest) && !['ringing', 'resolving', 'missed', 'notified', 'escalated'].includes(activeRequest.status))) {
      setActiveRequest(null)
      setTriedPractitionerIds([])
    }
    loadPreview([])
  }

  const handleClose = () => setAnchorEl(null)

  const trackRequest = useCallback(
    (request) => {
      setActiveRequest(request)
      onRequestChange?.(request)
    },
    [onRequestChange],
  )

  // After the softphone call ends, follow the request until the server settles it.
  const followOutcome = useCallback(
    (requestId) => {
      if (pollRef.current) clearInterval(pollRef.current)
      let attempts = 0
      pollRef.current = setInterval(async () => {
        attempts += 1
        const result = await getDeskCall(requestId)
        if (result?.ok && result?.deskCallRequest) {
          trackRequest(result.deskCallRequest)
          const settled = !['ringing', 'resolving'].includes(result.deskCallRequest.status)
          if (settled || attempts >= OUTCOME_POLL_MAX) {
            clearInterval(pollRef.current)
            pollRef.current = null
          }
        } else if (attempts >= OUTCOME_POLL_MAX) {
          clearInterval(pollRef.current)
          pollRef.current = null
        }
      }, OUTCOME_POLL_MS)
    },
    [trackRequest],
  )

  useEffect(() => {
    if (!activeRequest?.id) return
    if (callState?.status === 'ended' && ['ringing', 'resolving'].includes(activeRequest?.status)) {
      followOutcome(activeRequest.id)
    }
  }, [callState?.status, activeRequest?.id, activeRequest?.status, followOutcome])

  const candidates = useMemo(() => (Array.isArray(preview?.candidates) ? preview.candidates : []), [preview?.candidates])
  const primary = preview?.primary || null
  const selected = candidates.find((candidate) => candidate?.practitionerId === selectedId) || primary
  const nextCandidate = useMemo(
    () => candidates.find((candidate) => !triedPractitionerIds.includes(candidate?.practitionerId)) || null,
    [candidates, triedPractitionerIds],
  )

  const handleCall = async ({ skipIds = [], explicitPractitionerId = null, retryOfRequestId = null, callSupportLine = false } = {}) => {
    setPlacing(true)
    try {
      const chosen = explicitPractitionerId || (selected && selected.practitionerId !== primary?.practitionerId ? selected.practitionerId : null)
      const result = await placeDeskCall({
        clinicBrandId,
        clinicId,
        role,
        chamberRoomId,
        eventType,
        specialtySlug,
        doctorMentorId,
        bookedEventId,
        practitionerId: chosen || practitionerId || undefined,
        skipPractitionerIds: skipIds,
        retryOfRequestId,
        reason,
        callSupportLine,
      })
      if (result?.deskCallRequest) {
        trackRequest(result.deskCallRequest)
        if (result.deskCallRequest?.targetPractitionerId) {
          setTriedPractitionerIds((prev) => [...prev, result.deskCallRequest.targetPractitionerId])
        }
      }
      if (result?.nobodyOnDuty) {
        toast.info(result?.message || `No ${roleWord} is on duty right now.`)
      }
    } catch (error) {
      toast.error(getPortalVoiceFriendlyErrorMessage(error, 'Unable to place the call right now.'))
    } finally {
      setPlacing(false)
    }
  }

  const handleNotifyOnly = async (targetPractitionerId = null) => {
    setPlacing(true)
    try {
      const result = await startDeskCallRequest({
        clinicBrandId,
        clinicId,
        role,
        chamberRoomId,
        eventType,
        specialtySlug,
        doctorMentorId,
        bookedEventId,
        practitionerId: targetPractitionerId || practitionerId || undefined,
        skipPractitionerIds: targetPractitionerId ? [] : triedPractitionerIds,
        reason,
        notifyOnly: true,
      })
      if (!result?.ok) {
        toast.error(result?.message || 'Unable to send the WhatsApp message.')
        return
      }
      if (result?.nobodyOnDuty) {
        toast.info(result?.message || 'Nobody is on duty to message.')
        return
      }
      trackRequest(result?.deskCallRequest || null)
      toast.success(`${result?.deskCallRequest?.targetDisplayName || 'The practitioner'} has been messaged on WhatsApp.`)
    } finally {
      setPlacing(false)
    }
  }

  // ---- Announce ("Request screening") ----
  const handleAnnounce = async ({ explicitPractitionerId = null, retryOfRequestId = null, skipIds = [] } = {}) => {
    setPlacing(true)
    try {
      const chosen = explicitPractitionerId || (selected && selected.practitionerId !== primary?.practitionerId ? selected.practitionerId : null)
      const result = await sendScreeningCall({
        clinicBrandId,
        clinicId,
        role,
        chamberRoomId,
        eventType,
        specialtySlug,
        doctorMentorId,
        bookedEventId,
        practitionerId: chosen || practitionerId || undefined,
        skipPractitionerIds: skipIds,
        retryOfRequestId,
        reason,
      })
      if (!result?.ok) {
        toast.error(result?.message || 'Unable to send the screening call.')
        return
      }
      const request = result?.deskCallRequest || null
      if (request?.supportLine) {
        // The server routed this to the support line (nobody reachable): that needs the softphone.
        await hangUpDeskCall(request.id)
        toast.info('Nobody reachable to call. Use Call AUMHUM support instead.')
        loadPreview([])
        return
      }
      if (result?.nobodyOnDuty) {
        toast.info(result?.message || `No ${roleWord} is on duty right now.`)
      }
      if (request) {
        trackRequest(request)
        if (request.targetPractitionerId) {
          setTriedPractitionerIds((prev) => [...prev, request.targetPractitionerId])
        }
      }
    } finally {
      setPlacing(false)
    }
  }

  const handleJoinTalk = async () => {
    if (!activeRequest?.id) return
    setJoiningTalk(true)
    try {
      const result = await joinDeskTalk({ requestId: activeRequest.id, clinicBrandId, clinicId, role })
      if (result?.deskCallRequest) trackRequest(result.deskCallRequest)
    } catch (error) {
      toast.error(getPortalVoiceFriendlyErrorMessage(error, 'Unable to join the call from this screen.'))
    } finally {
      setJoiningTalk(false)
    }
  }

  const handleTalkPreference = async (_event, value) => {
    if (!value || value === talkPreference) return
    const previous = talkPreference
    setTalkPreference(value)
    const result = await setDeskTalkPreference({ clinicId, preference: value })
    if (!result?.ok) {
      setTalkPreference(previous)
      toast.error(result?.message || 'Unable to save the setting.')
    }
  }

  // Follow an announce request: the server call has no softphone state to watch, and a call-back can
  // come in after it rang out.
  useEffect(() => {
    if (!isAnnounceFollowable(activeRequest)) {
      if (announcePollRef.current) {
        clearInterval(announcePollRef.current)
        announcePollRef.current = null
      }
      return undefined
    }
    if (announcePollRef.current) return undefined
    const requestId = activeRequest.id
    let polls = 0
    announcePollRef.current = setInterval(async () => {
      polls += 1
      const result = await getDeskCall(requestId)
      if (result?.ok && result?.deskCallRequest) trackRequest(result.deskCallRequest)
      if (polls >= 900) {
        clearInterval(announcePollRef.current)
        announcePollRef.current = null
      }
    }, 3000)
    return undefined
  }, [activeRequest, trackRequest])
  useEffect(
    () => () => {
      if (announcePollRef.current) clearInterval(announcePollRef.current)
    },
    [],
  )

  const talkOnScreen = ['screen', 'both'].includes(activeRequest?.talkChannel)
  const talkPending = Boolean(
    activeRequest?.mode === 'announce' &&
      activeRequest?.talkRequestedAt &&
      !activeRequest?.talkJoinedAt &&
      !activeRequest?.endedAt &&
      talkOnScreen,
  )
  // The practitioner pressed 2: bring the popover back (it may have been closed) and chime once.
  useEffect(() => {
    const alertKey = `${activeRequest?.id}:${activeRequest?.talkRequestedAt}`
    if (!talkPending || talkAlertedRef.current === alertKey) return
    talkAlertedRef.current = alertKey
    if (!anchorEl && lastAnchorRef.current?.isConnected) setAnchorEl(lastAnchorRef.current)
    toast.info(`${activeRequest?.targetDisplayName || 'The practitioner'} wants to talk. Tap Talk now.`)
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext
      const ctx = new AudioCtx()
      const osc = ctx.createOscillator()
      const gain = ctx.createGain()
      osc.frequency.value = 880
      gain.gain.value = 0.08
      osc.connect(gain)
      gain.connect(ctx.destination)
      osc.start()
      osc.stop(ctx.currentTime + 0.35)
    } catch {
      // no audio
    }
  }, [talkPending, activeRequest?.id, activeRequest?.talkRequestedAt, activeRequest?.targetDisplayName, anchorEl])

  const [hangingUp, setHangingUp] = useState(false)
  const handleHangUp = async () => {
    setHangingUp(true)
    try {
      if (ACTIVE_CALL_STATES.includes(callState?.status)) {
        endPortalVoiceCall()
      }
      const requestId = activeRequest?.id
      if (requestId && ['resolving', 'ringing'].includes(activeRequest?.status)) {
        const result = await hangUpDeskCall(requestId)
        if (result?.ok && result?.deskCallRequest) {
          trackRequest(result.deskCallRequest)
        }
      }
    } finally {
      setHangingUp(false)
    }
  }

  // A practitioner with no number on their profile is not a blocker: the desk calls AUMHUM support instead.
  const selectedUnreachable = Boolean(selected && selected.reachable === false)
  const routeDisabledReason =
    preview?.route && preview.route.enabled === false && primary && !preview.route.practitionerUnreachable && !selectedUnreachable
      ? preview.route.disabledReason
      : ''
  const blocker =
    preview?.deskCallEnabled === false
      ? 'Desk calling is switched off for this clinic.'
      : preview?.permissionEnabled === false
        ? `Calling the ${roleWord} on duty is not enabled for your account.`
        : routeDisabledReason
  const softphoneBusy = ACTIVE_CALL_STATES.includes(callState?.status)
  const requestInFlight = Boolean(
    activeRequest && (['ringing', 'resolving'].includes(activeRequest.status) || isAnnounceLive(activeRequest)),
  )
  // The softphone bar belongs to softphone calls; an announce call is placed by the server.
  const showCallBar = softphoneBusy || (requestInFlight && activeRequest?.mode !== 'announce')
  const requestMissed = ['missed', 'notified', 'escalated'].includes(activeRequest?.status)
  const nobodyNow = Boolean(preview?.ok && !primary)
  const nobodyToday = nobodyNow && !preview?.nextRosterStartAt
  const supportFallback = Boolean(preview?.supportLineFallback)
  const triggerLabel = label || (isAnnounce ? `Request ${roleWord === 'screener' ? 'screening' : roleWord}` : `Call ${roleWord}`)
  const timeline = timelineOf(activeRequest)
  const retryCandidate = requestMissed && nextCandidate && nextCandidate.practitionerId !== activeRequest?.targetPractitionerId ? nextCandidate : null

  const primaryAction = (() => {
    if (requestInFlight) return null
    if (retryCandidate) {
      const retryOptions = { skipIds: triedPractitionerIds, explicitPractitionerId: retryCandidate.practitionerId, retryOfRequestId: activeRequest?.id || null }
      return {
        label: `Try ${firstNameOf(retryCandidate)} next`,
        onClick: () => (isAnnounce && retryCandidate.reachable !== false ? handleAnnounce(retryOptions) : handleCall(retryOptions)),
      }
    }
    if (nobodyNow) {
      return supportFallback && !blocker ? { label: 'Call AUMHUM support', support: true, onClick: () => handleCall({}) } : null
    }
    if (!selected) return null
    if (selectedUnreachable) {
      return supportFallback && !blocker
        ? { label: 'Call AUMHUM support', support: true, onClick: () => handleCall({ callSupportLine: true, retryOfRequestId: activeRequest?.id || null }) }
        : null
    }
    if (isAnnounce) {
      return {
        label: activeRequest ? `Send the screening call to ${firstNameOf(selected)} again` : `Send screening call to ${firstNameOf(selected)}`,
        onClick: () => handleAnnounce({ retryOfRequestId: activeRequest?.id || null }),
      }
    }
    return {
      label: activeRequest ? `Call ${firstNameOf(selected)} again` : `Call ${firstNameOf(selected)}`,
      onClick: () => handleCall({ retryOfRequestId: activeRequest?.id || null }),
    }
  })()
  const autoTalkLabel =
    micAvailable === true ? 'this screen (microphone found)' : micAvailable === false ? 'your phone (no microphone found)' : 'this screen'
  const talkHelp = {
    auto: `Auto: ${autoTalkLabel}.`,
    screen: 'You talk through this computer.',
    phone: 'We ring your desk phone.',
    both: 'This screen and your phone both ring; answer either.',
  }[talkPreference]
  const whatsappTarget = retryCandidate || (nobodyNow || selectedUnreachable ? null : selected)
  const reachableAlternative = selectedUnreachable
    ? candidates.find((candidate) => candidate?.reachable !== false && candidate?.practitionerId !== selected?.practitionerId)
    : null

  return (
    <>
      {typeof renderTrigger === 'function' ? (
        renderTrigger({ onClick: handleOpen, label: triggerLabel, busy: placing, request: activeRequest })
      ) : (
        <Button
          size={size}
          variant={variant}
          color="primary"
          fullWidth={fullWidth}
          startIcon={compact ? undefined : <PhoneInTalkRounded fontSize="small" />}
          onClick={handleOpen}
          sx={{ textTransform: 'none', fontWeight: 700, whiteSpace: 'nowrap', ...sx }}
          aria-label={label || `Call ${roleMeta.article} on duty`}
        >
          {compact ? <PhoneInTalkRounded fontSize="small" /> : triggerLabel}
        </Button>
      )}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{ paper: { sx: { width: 380, maxWidth: 'calc(100vw - 24px)', borderRadius: '16px', overflow: 'hidden' } } }}
      >
        {/* Header */}
        <Box sx={{ px: 2, pt: 1.75, pb: 1.25, background: 'linear-gradient(135deg, rgba(13,148,136,0.10), rgba(59,130,246,0.06))' }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: '16px', fontWeight: 800 }}>{heading || (isAnnounce ? `Request ${roleWord === 'screener' ? 'a screening' : `the ${roleWord}`}` : `Call the ${roleWord} on duty`)}</Typography>
              {contextLine || reason ? (
                <Typography sx={{ fontSize: '12.5px', color: 'rgba(0,0,0,0.62)', mt: 0.25 }}>{contextLine || reason}</Typography>
              ) : null}
              {preview?.clinicLocationName ? (
                <Typography sx={{ fontSize: '11.5px', color: 'rgba(0,0,0,0.45)', mt: 0.25 }}>{preview.clinicLocationName}</Typography>
              ) : null}
            </Box>
            <IconButton size="small" onClick={handleClose} aria-label="Close">
              <CloseRounded fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        <Box sx={{ px: 2, py: 1.5 }}>
          {previewLoading && !preview ? (
            <Box>
              <Skeleton variant="rounded" height={56} sx={{ borderRadius: '12px', mb: 1 }} />
              <Skeleton variant="rounded" height={40} sx={{ borderRadius: '10px' }} />
            </Box>
          ) : preview && !preview.ok ? (
            <Alert severity="error" sx={{ borderRadius: '12px' }}>
              {preview.message}
            </Alert>
          ) : (
            <>
              {/* Who will be called */}
              {!activeRequest || !requestInFlight ? (
                nobodyNow ? (
                  <Box sx={{ p: 1.5, borderRadius: '12px', backgroundColor: nobodyToday ? TONES.red.bg : TONES.amber.bg }}>
                    <Typography sx={{ fontSize: '15px', fontWeight: 800, color: nobodyToday ? TONES.red.fg : TONES.amber.fg }}>
                      {nobodyToday ? `No ${roleWord} available today` : `No ${roleWord} on duty right now`}
                    </Typography>
                    <Typography sx={{ fontSize: '12.5px', color: 'rgba(0,0,0,0.65)', mt: 0.25 }}>
                      {nobodyToday
                        ? `Nobody is checked in or rostered as ${roleMeta.article} for the rest of today.`
                        : `${preview?.nextRosterPractitionerName || 'The next one'} is rostered from ${formatClock(preview?.nextRosterStartAt)}.`}{' '}
                      {supportFallback ? 'AUMHUM support can arrange one: call them below.' : 'Ask AUMHUM support to arrange one.'}
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                    {candidates.slice(0, 4).map((candidate) => (
                      <CandidateRow
                        key={candidate.practitionerId}
                        candidate={candidate}
                        selected={candidate.practitionerId === selected?.practitionerId}
                        disabled={placing}
                        onSelect={() => setSelectedId(candidate.practitionerId)}
                      />
                    ))}
                    {candidates.length > 1 ? (
                      <Typography sx={{ fontSize: '11.5px', color: 'rgba(0,0,0,0.5)' }}>
                        The first one is who the system would call. Tap another to call them instead.
                      </Typography>
                    ) : null}
                  </Box>
                )
              ) : null}

              {selectedUnreachable && !requestInFlight ? (
                <Alert severity="info" sx={{ mt: 1.25, borderRadius: '12px', fontSize: '12.5px' }}>
                  <strong>{selected?.name}</strong> is not reachable on mobile: there is no number on their practitioner profile.{' '}
                  {supportFallback ? 'Call AUMHUM support below and they will reach them.' : 'Ask AUMHUM support to add their mobile number.'}
                  {reachableAlternative ? ` Or tap ${firstNameOf(reachableAlternative)} above to call them instead.` : ''}
                </Alert>
              ) : null}

              {blocker ? (
                <Alert severity="warning" sx={{ mt: 1.25, borderRadius: '12px', fontSize: '12.5px' }}>
                  {blocker} Ask AUMHUM support to enable it.
                </Alert>
              ) : null}

              {/* What happened so far */}
              {timeline.length > 0 ? (
                <Box sx={{ mt: 1.25, p: 1.25, borderRadius: '12px', backgroundColor: 'rgba(15, 23, 42, 0.03)', border: '1px solid rgba(0,0,0,0.06)' }}>
                  <Typography sx={{ fontSize: '12px', fontWeight: 800, color: 'rgba(0,0,0,0.55)', mb: 0.75 }}>
                    {activeRequest?.targetDisplayName ? `${activeRequest.targetDisplayName} · ${describeDeskCallStatus(activeRequest)}` : describeDeskCallStatus(activeRequest)}
                  </Typography>
                  {timeline.map((step, index) => (
                    <Box key={`${step.label}-${index}`} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', py: 0.4 }}>
                      <Box sx={{ pt: '1px' }}>
                        <StepIcon state={step.state} />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontSize: '13px', fontWeight: step.state === 'active' ? 700 : 600 }}>{step.label}</Typography>
                        {step.detail ? <Typography sx={{ fontSize: '12px', color: 'rgba(0,0,0,0.55)' }}>{step.detail}</Typography> : null}
                      </Box>
                      {step.time ? <Typography sx={{ fontSize: '11.5px', color: 'rgba(0,0,0,0.45)', whiteSpace: 'nowrap' }}>{formatClock(step.time)}</Typography> : null}
                    </Box>
                  ))}
                </Box>
              ) : null}

              {/* In-call controls: the desk hangs up here (the floating call panel is hidden on this page). */}
              {/* The practitioner pressed 2 on the screening call */}
              {talkPending && !softphoneBusy ? (
                <Box sx={{ mt: 1.25, p: 1.25, borderRadius: '12px', backgroundColor: TONES.green.bg, border: `1.5px solid ${TONES.green.ring}` }}>
                  <Typography sx={{ fontSize: '14px', fontWeight: 800, color: TONES.green.fg }}>
                    {activeRequest?.targetDisplayName || 'The practitioner'}{' '}
                    {activeRequest?.status === 'called-back' ? 'is calling you back' : 'is on the line and wants to talk'}
                  </Typography>
                  {activeRequest?.talkChannel === 'both' ? (
                    <Typography sx={{ fontSize: '12px', color: 'rgba(0,0,0,0.6)' }}>Your phone is ringing too; answer either.</Typography>
                  ) : null}
                  <Button
                    variant="contained"
                    fullWidth
                    disabled={joiningTalk}
                    startIcon={joiningTalk ? <CircularProgress size={16} color="inherit" /> : <PhoneInTalkRounded />}
                    onClick={handleJoinTalk}
                    sx={{ mt: 1, textTransform: 'none', fontWeight: 800, borderRadius: '10px', backgroundColor: '#047857', '&:hover': { backgroundColor: '#065f46' } }}
                  >
                    Talk now
                  </Button>
                </Box>
              ) : null}
              {activeRequest?.mode === 'announce' && activeRequest?.talkRequestedAt && !activeRequest?.talkJoinedAt && activeRequest?.talkChannel === 'phone' && !activeRequest?.endedAt ? (
                <Alert severity="info" sx={{ mt: 1.25, borderRadius: '12px', fontSize: '12.5px' }}>
                  {activeRequest?.targetDisplayName || 'The practitioner'} wants to talk. We are ringing your desk phone.
                </Alert>
              ) : null}

              {showCallBar ? (
                <Box
                  sx={{
                    mt: 1.25,
                    p: 1.25,
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    backgroundColor: callState?.status === 'connected' ? TONES.green.bg : TONES.blue.bg,
                    border: `1px solid ${callState?.status === 'connected' ? TONES.green.ring : TONES.blue.ring}`,
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: '14px', fontWeight: 800 }} noWrap>
                      {softphoneLabel(callState, requestInFlight)}
                    </Typography>
                    <Typography sx={{ fontSize: '11.5px', color: 'rgba(0,0,0,0.55)' }}>
                      {callState?.muted ? 'You are muted' : 'Talk through this computer'}
                    </Typography>
                  </Box>
                  {callState?.status === 'connected' || callState?.status === 'on-hold' ? (
                    <IconButton
                      size="small"
                      onClick={() => togglePortalVoiceMute()}
                      aria-label={callState?.muted ? 'Unmute' : 'Mute'}
                      sx={{ border: '1px solid rgba(0,0,0,0.15)', backgroundColor: '#fff' }}
                    >
                      {callState?.muted ? <MicOffRounded fontSize="small" /> : <MicRounded fontSize="small" />}
                    </IconButton>
                  ) : null}
                  <Button
                    variant="contained"
                    color="error"
                    size="small"
                    disabled={hangingUp || callState?.status === 'ending'}
                    startIcon={hangingUp ? <CircularProgress size={14} color="inherit" /> : <CallEndRounded />}
                    onClick={handleHangUp}
                    sx={{ textTransform: 'none', fontWeight: 800, borderRadius: '10px', whiteSpace: 'nowrap' }}
                  >
                    Hang up
                  </Button>
                </Box>
              ) : null}

              {/* How the desk talks if the practitioner presses 2 */}
              {isAnnounce && !requestInFlight && !nobodyNow && !selectedUnreachable ? (
                <Box sx={{ mt: 1.25 }}>
                  <Typography sx={{ fontSize: '12px', fontWeight: 700, color: 'rgba(0,0,0,0.6)', mb: 0.5 }}>
                    If {selected ? firstNameOf(selected) : 'they'} presses 2 to talk, reach me on
                  </Typography>
                  <ToggleButtonGroup size="small" exclusive fullWidth value={talkPreference} onChange={handleTalkPreference}>
                    <ToggleButton value="auto" sx={{ textTransform: 'none', fontSize: '12px', py: 0.4 }}>Auto</ToggleButton>
                    <ToggleButton value="screen" sx={{ textTransform: 'none', fontSize: '12px', py: 0.4 }}>This screen</ToggleButton>
                    <ToggleButton value="phone" sx={{ textTransform: 'none', fontSize: '12px', py: 0.4 }}>My phone</ToggleButton>
                    <ToggleButton value="both" sx={{ textTransform: 'none', fontSize: '12px', py: 0.4 }}>Both</ToggleButton>
                  </ToggleButtonGroup>
                  <Typography sx={{ fontSize: '11.5px', color: 'rgba(0,0,0,0.5)', mt: 0.4 }}>{talkHelp}</Typography>
                </Box>
              ) : null}

              {/* Actions */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mt: 1.5 }}>
                {primaryAction ? (
                  <Button
                    variant="contained"
                    size="large"
                    disabled={placing || previewLoading || Boolean(blocker) || softphoneBusy}
                    startIcon={placing ? <CircularProgress size={16} color="inherit" /> : primaryAction.support ? <SupportAgentRounded /> : <PhoneInTalkRounded />}
                    onClick={primaryAction.onClick}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 800,
                      fontSize: '15px',
                      borderRadius: '12px',
                      py: 1,
                      backgroundColor: primaryAction.support ? '#4338ca' : '#0f766e',
                      '&:hover': { backgroundColor: primaryAction.support ? '#3730a3' : '#115e59' },
                    }}
                  >
                    {primaryAction.label}
                  </Button>
                ) : null}
                {whatsappTarget && !requestInFlight ? (
                  <Button
                    variant="outlined"
                    size="medium"
                    disabled={placing || Boolean(blocker)}
                    startIcon={<WhatsAppIcon />}
                    onClick={() => handleNotifyOnly(whatsappTarget.practitionerId !== primary?.practitionerId || retryCandidate ? whatsappTarget.practitionerId : null)}
                    sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '12px', color: '#15803d', borderColor: 'rgba(21,128,61,0.4)' }}
                  >
                    WhatsApp {firstNameOf(whatsappTarget)} instead
                  </Button>
                ) : null}
              </Box>

              {/* Footer */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 1.5, color: 'rgba(0,0,0,0.5)' }}>
                <LockOutlined sx={{ fontSize: 14 }} />
                <Typography sx={{ fontSize: '11.5px' }}>
                  {isAnnounce
                    ? 'An automated call tells them the clinic, room and patient. 1 repeats it, 2 talks to you, 3 says on my way.'
                    : 'Numbers stay private. They hear “call from the front desk” and press 1 to accept.'}
                </Typography>
              </Box>
              <Button
                size="small"
                onClick={() => setShowOwnPhone((prev) => !prev)}
                endIcon={<ExpandMoreRounded sx={{ transform: showOwnPhone ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />}
                sx={{ textTransform: 'none', fontSize: '12px', fontWeight: 600, mt: 0.5, px: 0.5, color: 'rgba(0,0,0,0.6)' }}
              >
                Calling from your own phone instead?
              </Button>
              <Collapse in={showOwnPhone} unmountOnExit>
                <DeskCallerVerification clinicId={clinicId} />
              </Collapse>
            </>
          )}
        </Box>
      </Popover>
    </>
  )
}

DeskCallButton.propTypes = {
  clinicBrandId: PropTypes.string,
  clinicId: PropTypes.string,
  role: PropTypes.oneOf(['screener', 'therapist', 'doctor']),
  chamberRoomId: PropTypes.string,
  eventType: PropTypes.string,
  specialtySlug: PropTypes.string,
  doctorMentorId: PropTypes.string,
  bookedEventId: PropTypes.string,
  practitionerId: PropTypes.string,
  label: PropTypes.string,
  heading: PropTypes.string,
  reason: PropTypes.string,
  contextLine: PropTypes.string,
  size: PropTypes.string,
  variant: PropTypes.string,
  fullWidth: PropTypes.bool,
  compact: PropTypes.bool,
  sx: PropTypes.object,
  onRequestChange: PropTypes.func,
  renderTrigger: PropTypes.func,
  mode: PropTypes.oneOf(['call', 'announce']),
}
